import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Pagination} from 'lib/Listings';
import {GLOBAL_SEARCH_URL} from 'search/SearchConstants';
import * as net from 'util/Net';

import {STATUS_CODE_TO_STATUS} from './PredictionBatchTypes';
import type {PredictionBatchRow, SortConfig} from './PredictionBatchTypes';

export interface PredictionBatchSearchFilters {
  query: string;
  creatorId: string | null;
  predictionBatchId: string | null;
}

export const DEFAULT_FILTERS: PredictionBatchSearchFilters = {
  query: '',
  creatorId: null,
  predictionBatchId: null,
};

interface PredictionBatchListingData {
  id: string;
  name: string;
  modelName: string;
  statusCode: string;
  createdAt: string;
  modifiedAt: string;
  numPredictionJobs?: number | null;
  creator?: {name: string} | null;
  folder?: {name: string} | null;
}

interface SearchResponse {
  ids: Array<string>;
  total: number;
  isExact: boolean;
  predictionBatchesById?: Record<string, PredictionBatchListingData>;
}

function buildSearchFilters(filters: PredictionBatchSearchFilters): Array<Record<string, unknown>> {
  const searchFilters: Array<Record<string, unknown>> = [
    {key: 'TYPES', operator: 'IS_ONE_OF', value: [ModelTypeConstants.PREDICTION_BATCH]},
  ];

  if (filters.creatorId) {
    searchFilters.push({
      key: 'CREATOR',
      operator: 'IS_ONE_OF',
      value: [filters.creatorId],
    });
  }

  if (filters.predictionBatchId) {
    searchFilters.push({
      key: 'API_IDS',
      operator: 'IS_ONE_OF',
      value: [filters.predictionBatchId],
    });
  }

  return searchFilters;
}

function buildSort(sort: SortConfig): Array<{key: string; reverse: boolean}> {
  const sortKeyMap: Record<string, string> = {
    createdAt: 'created_at',
    modifiedAt: 'modified_at',
  };
  return [{key: sortKeyMap[sort.key], reverse: sort.direction === 'desc'}];
}

function mapListingToRow(id: string, listing: PredictionBatchListingData | undefined): PredictionBatchRow {
  if (!listing) {
    return {
      id,
      name: id,
      status: 'PENDING',
      modelName: '',
      creatorName: '',
      createdAt: '',
      modifiedAt: '',
      folderName: '',
      numPredictionJobs: null,
    };
  }
  return {
    id: listing.id,
    name: listing.name ?? id,
    status: STATUS_CODE_TO_STATUS[listing.statusCode] ?? 'PENDING',
    modelName: listing.modelName ?? '',
    creatorName: listing.creator?.name ?? '',
    createdAt: listing.createdAt ?? '',
    modifiedAt: listing.modifiedAt ?? '',
    folderName: listing.folder?.name ?? '',
    numPredictionJobs: listing.numPredictionJobs ?? null,
  };
}

interface UsePredictionBatchSearchResult {
  runs: Array<PredictionBatchRow>;
  pagination: Pagination;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePredictionBatchSearch(
  filters: PredictionBatchSearchFilters,
  sort: SortConfig,
  offset: number,
  limit: number
): UsePredictionBatchSearchResult {
  const [runs, setRuns] = React.useState<Array<PredictionBatchRow>>([]);
  const [pagination, setPagination] = React.useState<Pagination>({offset: 0, limit, total: 0});
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = React.useState(0);

  const refresh = React.useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const searchParams = {
      offset,
      limit,
      query: filters.query,
      filters: buildSearchFilters(filters),
      sorts: buildSort(sort),
    };

    net
      .postJSONPromise({url: GLOBAL_SEARCH_URL, data: searchParams})
      .then((response: SearchResponse) => {
        if (cancelled) {
          return;
        }
        const predictionBatchesById = response.predictionBatchesById ?? {};
        const rows = response.ids.map((id) => mapListingToRow(id, predictionBatchesById[id]));
        setRuns(rows);
        setPagination({
          offset,
          limit,
          total: response.total,
          isExact: response.isExact,
        });
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Search failed');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters, sort, offset, limit, refreshCounter]);

  return {runs, pagination, isLoading, error, refresh};
}
